// Phase B3 — operator-managed real testimonials. NEVER fabricated. Per
// CLAUDE.md + Override §6.3, sites only render Review/AggregateRating
// schema once we have at least 3 verified human-collected reviews
// (with FTC disclosure where applicable). This page is the CRUD that
// gates that.

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type Testimonial, type TestimonialInput, sitesApi, testimonialsApi } from "../lib/api";

export function TestimonialsPage() {
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [siteFilter, setSiteFilter] = useState<string>("");
  const [showAdd, setShowAdd] = useState(false);

  const sitesQ = useQuery({ queryKey: ["sites"], queryFn: sitesApi.list });
  const testimonialsQ = useQuery({
    queryKey: ["testimonials", siteFilter, statusFilter],
    queryFn: () => testimonialsApi.list(
      siteFilter || undefined,
      statusFilter === "all" ? undefined : statusFilter,
    ),
    refetchInterval: 60_000,
  });

  const list = testimonialsQ.data?.testimonials ?? [];
  const counts = {
    draft: list.filter((t) => t.status === "draft").length,
    approved: list.filter((t) => t.status === "approved").length,
    rejected: list.filter((t) => t.status === "rejected").length,
  };

  return (
    <div>
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <h1 className="text-2xl font-display font-bold">Testimonials</h1>
          <p className="text-ink-600 text-sm mt-1">
            Real customer reviews. <strong>Never fabricated.</strong> A site only renders the
            Reviews block + AggregateRating schema once it has ≥3 approved testimonials.
          </p>
        </div>
        <button
          onClick={() => setShowAdd((s) => !s)}
          className="px-4 py-2 bg-ink-900 text-white rounded-md font-medium text-sm hover:bg-ink-800"
        >
          {showAdd ? "Cancel" : "+ Add testimonial"}
        </button>
      </div>

      <div className="flex gap-3 mb-4 text-sm">
        <Pill label="Approved" value={counts.approved} tone="green" />
        <Pill label="Draft" value={counts.draft} tone="amber" />
        <Pill label="Rejected" value={counts.rejected} tone="grey" />
      </div>

      {showAdd && (
        <AddForm
          sites={sitesQ.data?.sites ?? []}
          onClose={() => setShowAdd(false)}
        />
      )}

      <div className="bg-white rounded-xl border border-ink-100 p-4 mb-4 flex flex-wrap gap-3 items-end">
        <label className="flex flex-col gap-1">
          <span className="text-xs uppercase tracking-wider text-ink-500 font-medium">Site</span>
          <select
            value={siteFilter}
            onChange={(e) => setSiteFilter(e.target.value)}
            className="px-3 py-2 border border-ink-200 rounded-md text-sm bg-white min-w-[16rem]"
          >
            <option value="">All sites (incl. portfolio-wide)</option>
            {(sitesQ.data?.sites ?? []).map((s) => (
              <option key={s.id} value={s.id}>{s.domain}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs uppercase tracking-wider text-ink-500 font-medium">Status</span>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2 border border-ink-200 rounded-md text-sm bg-white">
            <option value="all">all</option>
            <option value="draft">draft</option>
            <option value="approved">approved</option>
            <option value="rejected">rejected</option>
          </select>
        </label>
      </div>

      {testimonialsQ.isLoading && <Loading />}
      {testimonialsQ.error && <ErrorBox msg={String((testimonialsQ.error as Error).message)} />}
      {testimonialsQ.data && list.length === 0 && (
        <div className="bg-white rounded-xl border border-ink-100 p-8 text-center">
          <p className="text-ink-700 font-medium">No testimonials match this filter.</p>
          <p className="text-ink-500 text-sm mt-1">
            Add testimonials as you collect them from live customers (post-call surveys, email asks
            after a job converts). Per FTC: include disclosure if customer received any incentive.
          </p>
        </div>
      )}
      <div className="space-y-3">
        {list.map((t) => <TestimonialCard key={t.id} testimonial={t} />)}
      </div>
    </div>
  );
}

function AddForm({
  sites,
  onClose,
}: {
  sites: Array<{ id: string; domain: string }>;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState<TestimonialInput>({
    customerName: "",
    rating: 5,
    quote: "",
  });
  const create = useMutation({
    mutationFn: testimonialsApi.create,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["testimonials"] });
      onClose();
    },
  });
  return (
    <form
      className="bg-white rounded-xl border border-ink-200 p-4 mb-4 space-y-3"
      onSubmit={(e) => { e.preventDefault(); create.mutate(form); }}
    >
      <h3 className="text-sm font-display font-bold uppercase tracking-wider text-ink-700">Add testimonial</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Customer name (e.g. Sarah K.)" required>
          <input
            value={form.customerName}
            onChange={(e) => setForm({ ...form, customerName: e.target.value })}
            placeholder="First name + last initial"
            className="w-full px-2 py-1.5 border border-ink-200 rounded text-sm"
            required
          />
        </Field>
        <Field label="Customer city">
          <input
            value={form.customerCity ?? ""}
            onChange={(e) => setForm({ ...form, customerCity: e.target.value })}
            placeholder="Where the customer lives"
            className="w-full px-2 py-1.5 border border-ink-200 rounded text-sm"
          />
        </Field>
        <Field label="Site (optional — null = portfolio-wide)">
          <select
            value={form.siteId ?? ""}
            onChange={(e) => setForm({ ...form, siteId: e.target.value || undefined })}
            className="w-full px-2 py-1.5 border border-ink-200 rounded text-sm bg-white"
          >
            <option value="">— portfolio-wide —</option>
            {sites.map((s) => (<option key={s.id} value={s.id}>{s.domain}</option>))}
          </select>
        </Field>
        <Field label="Service slug (optional)">
          <input
            value={form.serviceSlug ?? ""}
            onChange={(e) => setForm({ ...form, serviceSlug: e.target.value })}
            placeholder="e.g. emergency-plumbing"
            className="w-full px-2 py-1.5 border border-ink-200 rounded text-sm"
          />
        </Field>
        <Field label="Rating" required>
          <select
            value={form.rating}
            onChange={(e) => setForm({ ...form, rating: parseInt(e.target.value, 10) })}
            className="w-full px-2 py-1.5 border border-ink-200 rounded text-sm bg-white"
          >
            {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n} star{n > 1 ? "s" : ""}</option>)}
          </select>
        </Field>
        <Field label="Source URL (review screenshot / email)">
          <input
            value={form.sourceUrl ?? ""}
            onChange={(e) => setForm({ ...form, sourceUrl: e.target.value })}
            placeholder="https://…"
            className="w-full px-2 py-1.5 border border-ink-200 rounded text-sm"
          />
        </Field>
      </div>
      <Field label="Quote" required>
        <textarea
          value={form.quote}
          onChange={(e) => setForm({ ...form, quote: e.target.value })}
          rows={3}
          required
          placeholder="Customer's verbatim words"
          className="w-full px-2 py-1.5 border border-ink-200 rounded text-sm"
        />
      </Field>
      <Field label="Provenance notes (FTC audit trail)">
        <textarea
          value={form.provenanceNotes ?? ""}
          onChange={(e) => setForm({ ...form, provenanceNotes: e.target.value })}
          rows={2}
          placeholder="Where this came from, when collected, any verification you did"
          className="w-full px-2 py-1.5 border border-ink-200 rounded text-sm"
        />
      </Field>
      <div className="space-y-2">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.incentivized ?? false}
            onChange={(e) => setForm({ ...form, incentivized: e.target.checked })}
          />
          <span>Customer received an incentive (gift card, discount, free service)</span>
        </label>
        {form.incentivized && (
          <Field label="FTC disclosure text (REQUIRED when incentivized)" required>
            <input
              value={form.ftcDisclosureText ?? ""}
              onChange={(e) => setForm({ ...form, ftcDisclosureText: e.target.value })}
              placeholder="e.g. 'Customer received a $25 gift card for sharing their experience.'"
              className="w-full px-2 py-1.5 border border-ink-200 rounded text-sm"
              required
            />
          </Field>
        )}
      </div>
      <div className="flex gap-2 pt-2">
        <button
          type="submit"
          disabled={create.isPending}
          className="px-4 py-2 bg-ink-900 text-white rounded-md font-medium text-sm disabled:opacity-50"
        >
          {create.isPending ? "Saving…" : "Save as draft"}
        </button>
        <button type="button" onClick={onClose} className="px-3 py-2 text-ink-500 text-sm">Cancel</button>
        {create.error && <span className="text-red-700 text-sm self-center">{(create.error as Error).message}</span>}
      </div>
    </form>
  );
}

function TestimonialCard({ testimonial }: { testimonial: Testimonial }) {
  const qc = useQueryClient();
  const update = useMutation({
    mutationFn: (patch: Parameters<typeof testimonialsApi.update>[1]) => testimonialsApi.update(testimonial.id, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["testimonials"] }),
  });
  const remove = useMutation({
    mutationFn: () => testimonialsApi.remove(testimonial.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["testimonials"] }),
  });
  const tone: Record<string, string> = {
    draft: "bg-amber-100 text-amber-800",
    approved: "bg-green-100 text-green-800",
    rejected: "bg-ink-100 text-ink-500",
  };
  return (
    <div className="bg-white rounded-xl border border-ink-100 p-4">
      <div className="flex items-baseline justify-between gap-3 mb-2">
        <div className="flex items-baseline gap-2">
          <span className="font-medium text-ink-900">{testimonial.customerName}</span>
          {testimonial.customerCity && <span className="text-xs text-ink-500">· {testimonial.customerCity}</span>}
          <span className="text-amber-600 text-sm">{"★".repeat(testimonial.rating)}{"☆".repeat(5 - testimonial.rating)}</span>
        </div>
        <span className={`inline-flex px-2 py-0.5 text-xs rounded-md font-bold ${tone[testimonial.status]}`}>
          {testimonial.status}
        </span>
      </div>
      <p className="text-sm text-ink-700 leading-relaxed mb-2">"{testimonial.quote}"</p>
      <div className="text-xs text-ink-500 space-y-0.5">
        {testimonial.siteDomain && <div>Site: {testimonial.siteDomain}</div>}
        {testimonial.serviceSlug && <div>Service: {testimonial.serviceSlug}</div>}
        {testimonial.sourceUrl && (
          <div>Source: <a href={testimonial.sourceUrl} target="_blank" rel="noreferrer" className="text-accent">link ↗</a></div>
        )}
        {testimonial.incentivized && testimonial.ftcDisclosureText && (
          <div className="text-amber-700">⚠ Incentivized: {testimonial.ftcDisclosureText}</div>
        )}
      </div>
      <div className="border-t border-ink-100 mt-3 pt-2 flex gap-2">
        {testimonial.status !== "approved" && (
          <button
            onClick={() => update.mutate({ status: "approved" })}
            disabled={update.isPending}
            className="px-2 py-1 bg-green-600 text-white rounded text-xs font-medium disabled:opacity-50"
          >Approve</button>
        )}
        {testimonial.status !== "rejected" && (
          <button
            onClick={() => update.mutate({ status: "rejected" })}
            disabled={update.isPending}
            className="px-2 py-1 bg-ink-300 text-ink-700 rounded text-xs font-medium disabled:opacity-50"
          >Reject</button>
        )}
        {testimonial.status !== "draft" && (
          <button
            onClick={() => update.mutate({ status: "draft" })}
            disabled={update.isPending}
            className="px-2 py-1 bg-ink-100 text-ink-700 rounded text-xs font-medium disabled:opacity-50"
          >Back to draft</button>
        )}
        <button
          onClick={() => { if (confirm("Delete this testimonial permanently?")) remove.mutate(); }}
          disabled={remove.isPending}
          className="ml-auto px-2 py-1 text-red-700 text-xs font-medium disabled:opacity-50"
        >Delete</button>
      </div>
    </div>
  );
}

function Pill({ label, value, tone }: { label: string; value: number; tone: "green" | "amber" | "grey" }) {
  const cls = tone === "green" ? "bg-green-100 text-green-800"
            : tone === "amber" ? "bg-amber-100 text-amber-800"
            : "bg-ink-100 text-ink-700";
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${cls}`}>
      <span className="font-bold">{value}</span>
      <span>{label}</span>
    </span>
  );
}

function Field({ label, children, required }: { label: string; children: React.ReactNode; required?: boolean }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs uppercase tracking-wider text-ink-500 font-medium">
        {label}{required && <span className="text-red-600 ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}

function Loading() {
  return <div className="bg-white rounded-xl border border-ink-100 p-8 text-center text-ink-500">Loading…</div>;
}
function ErrorBox({ msg }: { msg: string }) {
  return <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-4">Failed: {msg}</div>;
}
